import { Component, inject, Inject } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatError, MatInput } from '@angular/material/input';
import {
  API_DATE_FORMAT,
  DATE_FORMAT,
} from '../../../constants/date.constants';
import moment from 'moment';
import { SingleActionDialogComponent } from '../single-action-dialog/single-action-dialog.component';
import { MaskEmailPipe } from '../../../../pipes/mask-email.pipe';
import { NgClass } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { NumericOnlyDirective } from '../../../../directives/numeric-only.directive';
import { AlphaNumericOnlyDirective } from '../../../../directives/alpha-numeric-only.directive';
import { SetPasswordService } from '../../../services/set-password.service';
import { Router } from '@angular/router';
import { SharedStorageService } from '../../../services/shared-storage.service';
import { CreateMemberRequest } from '../../../interfaces/set-password.model';
import { STORAGE_KEY } from '../../../constants/store-key.constants';
import { LOGIN_REGISTER_TABS } from '../../../constants/common.constants';
import {
  birthdayValidator,
  idNoValidator,
} from '../../../utils/validator.util';

@Component({
  selector: 'app-forgot-password-dialog',
  standalone: true,
  imports: [
    TranslatePipe,
    FormsModule,
    MatInput,
    ReactiveFormsModule,
    MatError,
    NgClass,
    MatButton,
    NumericOnlyDirective,
    AlphaNumericOnlyDirective,
  ],
  templateUrl: './forgot-password-dialog.component.html',
  styleUrl: './forgot-password-dialog.component.scss',
})
export class ForgotPasswordDialogComponent {
  constructor(public dialogRef: MatDialogRef<ForgotPasswordDialogComponent>) {}

  dialog: MatDialog = inject(MatDialog);
  translateService: TranslateService = inject(TranslateService);
  setPasswordService: SetPasswordService = inject(SetPasswordService);
  sharedStorageService: SharedStorageService = inject(SharedStorageService);
  router: Router = inject(Router);
  maskEmailPipe = new MaskEmailPipe();
  showErrorMessage: boolean = false; // 是否顯示錯誤訊息

  // 生日三欄輸入（西元年 / 月 / 日）
  birthYear: string = '';
  birthMonth: string = '';
  birthDay: string = '';
  // 三欄都填完後，才即時顯示生日錯誤
  birthdayFilled: boolean = false;

  idNumberMaxLength: number = 10; // 身分證號最大長度

  forgotPasswordForm = new FormGroup({
    birthday: new FormControl<moment.Moment | null>(null, [
      Validators.required,
      (control: FormControl) =>
        birthdayValidator(control, this.translateService, {
          max: 100,
          min: 18,
        }),
    ]),
    idNumber: new FormControl('', [
      Validators.required,
      (control: FormControl) => idNoValidator(control, this.translateService),
    ]),
  });

  /**
   * 生日三欄（年/月/日）任一欄變動時，組出日期並寫回 birthday FormControl。
   */
  onBirthdayPartChange(): void {
    const year = this.birthYear.trim();
    const month = this.birthMonth.trim();
    const day = this.birthDay.trim();
    const control = this.forgotPasswordForm.get('birthday');

    this.birthdayFilled = !!year && !!month && !!day;

    if (!year || !month || !day) {
      control?.setValue(null);
      control?.markAsTouched();
      return;
    }

    // 補零成 YYYY/MM/DD，moment 嚴格模式驗證（可正確判斷閏年、大小月）
    const dateStr = `${year.padStart(4, '0')}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
    const parsed = moment(dateStr, DATE_FORMAT, true);

    control?.setValue(parsed.isValid() ? parsed : moment.invalid());
    control?.markAsTouched();
    control?.updateValueAndValidity();
  }

  /**
   * 生日欄位填滿時自動聚焦下一欄。年填滿 4 碼 → 跳月；月填滿 2 碼 → 跳日。
   */
  autoFocusNext(field: 'year' | 'month', nextInput: HTMLInputElement): void {
    const filled =
      field === 'year' ? this.birthYear.trim().length === 4 : this.birthMonth.trim().length === 2;
    if (filled && nextInput && !nextInput.disabled) {
      nextInput.focus();
    }
  }

  /** 三欄是否都為空 */
  get isBirthdayAllEmpty(): boolean {
    return !this.birthYear.trim() && !this.birthMonth.trim() && !this.birthDay.trim();
  }

  /** 送出後仍三欄皆空 → 三欄一起標紅、顯示年份提示 */
  get showBirthdayEmptyError(): boolean {
    return this.showErrorMessage && this.isBirthdayAllEmpty;
  }

  /** 年份是否不合法（已填但非 4 碼、或超出 1900~今年） */
  get isYearInvalid(): boolean {
    const year = this.birthYear.trim();
    if (!year) return false;
    const y = Number(year);
    return year.length !== 4 || isNaN(y) || y < 1900 || y > moment().year();
  }

  /** 月份是否不合法（已填但不在 1~12） */
  get isMonthInvalid(): boolean {
    const month = this.birthMonth.trim();
    if (!month) return false;
    const m = Number(month);
    return isNaN(m) || m < 1 || m > 12;
  }

  /** 日期是否不合法（已填但不在 1~該年月天數；閏年、大小月由 moment 判斷） */
  get isDayInvalid(): boolean {
    const day = this.birthDay.trim();
    if (!day) return false;
    const d = Number(day);
    if (isNaN(d) || d < 1) return true;

    if (!this.isYearInvalid && !this.isMonthInvalid && this.birthYear.trim() && this.birthMonth.trim()) {
      const daysInMonth = moment(
        `${this.birthYear.trim().padStart(4, '0')}-${this.birthMonth.trim().padStart(2, '0')}`,
        'YYYY-MM'
      ).daysInMonth();
      return d > daysInMonth;
    }
    return d > 31;
  }

  // 獲取驗證生日錯誤訊息
  getBirthdayErrorMessage() {
    const control = this.forgotPasswordForm.get('birthday');
    if (!control || !control.errors || (!this.showErrorMessage && !this.birthdayFilled)) return '';

    const errors = control.errors;

    // mat-datepickerParse錯誤
    if (errors['matDatepickerParse']) {
      return this.translateService.instant('error.birthday-error.format');
    }
    if (errors['required']) {
      return this.translateService.instant('error.birthday-error.required');
    }
    if (errors['invalidFormat']) {
      return errors['invalidFormat'];
    }
    return '';
  }

  // 取得身份證號錯誤訊息
  getIdNumberErrorMessage() {
    const control = this.forgotPasswordForm.get('idNumber');
    if (!control || !control.errors || !this.showErrorMessage) return '';

    const errors = control.errors;
    if (errors['required']) {
      return this.translateService.instant('error.id-number-error.required');
    }
    if (errors['invalidCharacters']) {
      return errors['invalidCharacters'];
    }
  }

  // 關閉當前dialog
  closeDialog() {
    this.dialogRef.close();
  }

  onSubmit() {
  this.showErrorMessage = true;

  if (this.forgotPasswordForm.valid) {
    this.showErrorMessage = false;

    const birthday =
      this.forgotPasswordForm
        .get('birthday')
        ?.value?.format(API_DATE_FORMAT) || '';
    const idNo = this.forgotPasswordForm.get('idNumber')?.value || '';
    const request = { birthday, idNo };
    
    this.setPasswordService.sendForgetPassword(request).subscribe({
      next: (res) => {
        // if find match, send reset password link to register email
        if (res.statusCode === 'S0000') {
          this.dialog.open(SingleActionDialogComponent, {
            disableClose: true,
            data: {
              title: this.translateService.instant(
                'forgot-password-dialog.already-send-email-title',
                { email: this.maskEmailPipe.transform(res.data?.email || '') }
              ),
              message: this.translateService.instant(
                'forgot-password-dialog.already-send-email-message'
              ),
              showCloseButton: false,
              buttonText: this.translateService.instant(
                'common.button.confirm'
              ),
              action: () => this.dialog.closeAll(),
            },
          });
        } else if (res.statusCode === 'E9011') {
          this.dialog.open(SingleActionDialogComponent, {
            // if this id isn't found in system let user go to register
            disableClose: true,
            data: {
              title: this.translateService.instant(
                'forgot-password-dialog.not-register-title'
              ),
              message: this.translateService.instant(
                'forgot-password-dialog.not-register-message'
              ),
              showCloseButton: true,
              buttonText: this.translateService.instant(
                'register-verify-contact.register'
              ),
              action: () => {
                // being nice put birthday and id directly fill in register form
                const createMemberRequest: CreateMemberRequest = {
                  gender: '',
                  birthday,
                  name: '',
                  idNo,
                  cell: '',
                  email: '',
                  password: '',
                  confirmPassword: '',
                };
                this.sharedStorageService.set(
                  STORAGE_KEY.CREATE_MEMBER_REQUEST,
                  createMemberRequest
                );

                this.router
                  .navigateByUrl('/healthcheck', { skipLocationChange: true })
                  .then(() => {
                    this.router
                      .navigate(['/healthcheck/login'], {
                        queryParams: { tab: LOGIN_REGISTER_TABS.REGISTER },
                      })
                      .then(() => this.dialog.closeAll());
                  });
              },
            },
          });
        } else if (res.statusCode === 'EC1004') {
            this.dialog.open(SingleActionDialogComponent, {
              disableClose: true,
              data: {
                title: this.translateService.instant(
                  'forgot-password-dialog.not-match-title'
                ),
                message: this.translateService.instant(
                  'forgot-password-dialog.not-match-message'  
                ),
                showCloseButton: false,
                buttonText: this.translateService.instant(
                  'common.button.confirm'
                ),
                action: () => this.dialog.closeAll(),
              },
            });
          }else {
          const noMatchDialog = this.dialog.open(SingleActionDialogComponent, {
            // if id and birth doesn't match, just pop up dialog
            disableClose: true,
            data: {
              title: this.translateService.instant(
                'forgot-password-dialog.not-match-title'
              ),
              message: this.translateService.instant(
                'forgot-password-dialog.not-match-message'
              ),
              showCloseButton: false,
              buttonText: this.translateService.instant(
                'common.button.confirm'
              ),
              action: () => noMatchDialog.close(),
            },
          });
        }
      },
      error: () => {
        // if send email failed, pop up dialog
        this.dialog.open(SingleActionDialogComponent, {
          disableClose: true,
          data: {
            title: this.translateService.instant(
              'forgot-password-dialog.send-email-failed-title'
            ),
            message: this.translateService.instant(
              'forgot-password-dialog.send-email-failed-message'
            ),
            showCloseButton: false,
            buttonText: this.translateService.instant(
              'common.button.confirm'
            ),
            action: () => this.dialog.closeAll(),
          },
        });
      }
    });
  }
}

  protected readonly DATE_FORMAT = DATE_FORMAT;
}
